import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useIsMobile from "../Hooks/useIsMobile";
import apiService from "../Services/api.service";
import CustomButton from "../components/Button";
import CustomCardList from "../components/Cards";
import PageLoader from "../components/Loader";
import Title from "../components/PageTitle";
import CustomTable from "../components/Table";
import { useToast } from "../components/Toast";
const EngagementTracker = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(searchParams.get("limit")) || 10
  );
  const [totalCount, setTotalCount] = useState(0); // Total number of doctors (for pagination)
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [Loader, isLoader] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const [typingTimeout, setTypingTimeout] = useState(null);
  const FIXED_STAGES = [
    "Contacted",
    "Engage demo",
    "Camp",
    "Influencer Activity",
    "Other",
  ];
  const [stage, setStage] = useState(searchParams.get("stage") || "");
  const [customStage, setCustomStage] = useState(searchParams.get("customStage") || "");
  

  const updateURLParams = (newFilters) => {
    setSearchParams({
      q: newFilters.searchQuery || "",
      page: newFilters.page || 1,
      limit: newFilters.rowsPerPage || 10,
      stage: stage || "",
      customStage: customStage || "",
    });
  };
  useEffect(() => {
    setSearchParams({
      q: searchQuery || "",
      page: page || 1,
      limit: rowsPerPage || 10,
      stage: stage || "",
      customStage: customStage || "",
    });
  }, [searchQuery, page, rowsPerPage, stage, customStage]);
  
  useEffect(() => {
    getDoctors(page, rowsPerPage); // Fetch data on page or rowsPerPage change
  }, [page, rowsPerPage]); // Add dependencies

  const getDoctors = async (page, limit) => {
    isLoader(true);
    try {
      const selectedStage = stage === "Other" ? customStage : stage;

      const { mappedDoctors, count } = await apiService.fetchDoctors(
        auth,
        page,
        limit,
        searchQuery,
        selectedStage
      );

      setFilteredDoctors(mappedDoctors);

      setTotalCount(count); // Set the total count
      isLoader(false);
    } catch (error) {
      Toast.error("Error while Fetching");
    } finally {
      isLoader(false);
    }
  };
  useEffect(() => {
    if (typingTimeout) clearTimeout(typingTimeout);

    const timeout = setTimeout(async () => {
      try {
        const selectedStage = stage === "Other" ? customStage : stage;

        const { mappedDoctors, count } = await apiService.fetchDoctors(
          auth,
          page,
          10,
          searchQuery,
          selectedStage
        );

        setTotalCount(count);
        setFilteredDoctors(mappedDoctors);
      } catch (error) {
        console.error("Error fetching Kiosk:", error);
        Toast.error("Error while Fetching");
      }
    }, 500); // 500ms debounce

    setTypingTimeout(timeout);

    // Cleanup
    return () => clearTimeout(timeout);
  }, [searchQuery, page, stage, customStage]);
  const handleSearch = (event) => {
    const keyword = event.target.value.toLowerCase();
    setSearchQuery(keyword); // Only set the query
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1); // Set the new page
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10)); // Update rows per page when changed
    setPage(1); // Reset to page 1 when rows per page change
  };
  const formatDate = (dateString) => {
    if (dateString == "NA" || dateString == "Invalid Date") return "NA";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const detailsClick = (row) => {
    navigate(`/dashboard/engagementTracker/${row.id}`, {
      state: {
        doctorId: row.id,
        doctorName: row.name,
        docMobile: row.mobile,
      },
    });
  };
  const fields = [
    { label: "Mobile", key: "mobile" },
    { label: "Username", key: "username" },
  ];

  const columns = [
    { field: "name", headerName: "Name", align: "center" },
    { field: "mobile", headerName: "Mobile", align: "center" },
  ];
  return (
    <div>
      <Paper sx={{ padding: 2 }}>
        <Title text="Doctors" />

        <Box sx={{ mb: 2 }}>
          {location.pathname === "/dashboard/doctor" && (
            <CustomButton
              width="200px"
              startIcon={<PersonAddIcon />}
              text="Add Doctor"
              onClick={() => navigate("/dashboard/doctor/add-doctor")}
            />
          )}

          {/* ======== SEARCH + STAGE IN SAME ROW ======== */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 3,
              width: "100%",
            }}
          >
            {/* Search field = 65% width */}
            <TextField
              label="Search"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearch}
              sx={{ width: stage === "Other" ? "40%" : "65%" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Stage dropdown = remaining width */}
            <TextField
              select
              label="Filter"
              value={stage}
              onChange={(e) => {
                setStage(e.target.value);
                if (e.target.value !== "Other") setCustomStage("");
              }}
              sx={{ width: stage === "Other" ? "30%" : "35%" }}
            >
              {FIXED_STAGES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            {/* Custom stage (only when Other is selected) */}
            {stage === "Other" && (
              <TextField
                label="Custom Stage"
                value={customStage}
                onChange={(e) => setCustomStage(e.target.value)}
                sx={{ width: "30%" }}
              />
            )}
          </Box>
        </Box>

        {isMobile ? (
          <CustomCardList
            page={page - 1}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            fields={fields}
            data={filteredDoctors}
            onCardClick={detailsClick}
          />
        ) : (
          <CustomTable
            columns={columns}
            data={filteredDoctors}
            onRowClick={detailsClick}
            actions={[
              {
                Component: CustomButton,
                props: { text: "Details", width: "90px", height: "40px" },
                onClick: (row) => detailsClick(row),
              },
            ]}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        )}
      </Paper>

      {Loader && <PageLoader />}
    </div>
  );
};

export default EngagementTracker;
